import SysRecord from 'app/class/record';
import { useFormContext } from 'react-hook-form';
import useRecordProducerActionCompiler from 'modules/record/record-producer/hooks/useRecordProducerActionCompiler';
import { useState } from 'react';
import useRecordProducerRelatedActions from 'modules/record/record-producer/hooks/useRecordProducerRelatedActions';
import { useSession } from 'app/hooks/useSession';
import FormActionConfirmationDialog, { requiresFormActionConfirmation } from 'modules/record/shared/components/FormActionConfirmationDialog';
import ResponsiveActionBar from 'modules/record/shared/components/ResponsiveActionBar';
import useActionHotkeys from 'modules/record/shared/hooks/useActionHotkeys';

interface RecordProducerRelatedActionsComponentProps {
    tableName: string;
    id?: string;
}

export const RecordProducerRelatedActionsComponent = ({ tableName, id }: RecordProducerRelatedActionsComponentProps) => {
    const { getValues } = useFormContext();
    const { data: userSession } = useSession();
    const { actions } = useRecordProducerRelatedActions(tableName, {
        form: getValues(),
        currentTableName: tableName,
        isNewRecord: !id,
        user: { id: userSession?.id || "", roles: userSession?.roles || [] }
    });
    const { runActionScript } = useRecordProducerActionCompiler();


    const [pendingAction, setPendingAction] = useState<SysRecord | null>(null);
    const [runningActionId, setRunningActionId] = useState<string | null>(null);

    const executeAction = async (action: SysRecord) => {
        setRunningActionId(action.id);

        try {
            await runActionScript(action, tableName, "related_form");
        } finally {
            setRunningActionId(null);
        }
    };

    const handleActionClick = (action: SysRecord) => {
        if (runningActionId) return;

        if (requiresFormActionConfirmation(action)) {
            setPendingAction(action);
            return;
        }

        executeAction(action);
    };

    const handleConfirmAction = async () => {
        const action = pendingAction;
        setPendingAction(null);

        if (action) {
            await executeAction(action);
        }
    };

    useActionHotkeys({
        actions,
        runningActionId,
        onActionTrigger: handleActionClick,
    });

    if (!actions.length) {
        return null;
    }

    return (
        <>
            <ResponsiveActionBar
                actions={actions}
                runningActionId={runningActionId}
                mobileVisibleCount={0}
                desktopVisibleCount={0}
                menuLabel="Related actions"
                mobileMenuLabel="Related"
                showMobileMenuLabel
                variant="inline"
                onActionClick={handleActionClick}
            />
            <FormActionConfirmationDialog
                action={pendingAction}
                open={Boolean(pendingAction)}
                onCancel={() => setPendingAction(null)}
                onConfirm={handleConfirmAction}
            />
        </>
    );
}
